import {
    Modal, ModalDialog, ModalClose, Typography, Box, Button, Stack,
    CircularProgress, Chip,
} from '@mui/joy';
import SmartAvatar from './SmartAvatar';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '');

/**
 * Выбор администратора для чека. Кандидатов присылает сервер
 * (GET /orders/<id>/admin-candidates/) — он же решает, кто доступен:
 *   рабочему аккаунту — только админы сегодняшнего графика его точки;
 *   руководителю — плюс все действующие админы вне графика (off_schedule).
 * Логики доступа на клиенте нет. Управляется хуком useOrderAdmin.
 */
export default function ChangeAdminModal({ open, loading, saving, data, onSelect, onClose }) {
    const candidates  = data?.candidates || [];
    const onSchedule  = candidates.filter(c => !c.off_schedule);
    const offSchedule = candidates.filter(c =>  c.off_schedule);
    const currentId   = data?.current_administrator_id ?? null;

    const renderBtn = (a) => {
        const isCurrent = currentId === a.id;
        return (
            <Button
                key={a.id}
                variant={isCurrent ? 'solid' : 'outlined'}
                color={isCurrent ? 'primary' : 'neutral'}
                disabled={saving}
                onClick={() => onSelect(a.id)}
                startDecorator={
                    <SmartAvatar src={a.avatar_url} size="sm" sx={{ width: 24, height: 24 }}>
                        {(a.full_name || '?').charAt(0).toUpperCase()}
                    </SmartAvatar>
                }
                sx={{ justifyContent: 'flex-start', fontWeight: 700, fontSize: 13 }}
            >
                <Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>{a.full_name}</Box>
                {a.start_time && (
                    <Chip size="sm" variant="soft" color="neutral"
                        sx={{ fontSize: 9, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                        с {a.start_time}
                    </Chip>
                )}
            </Button>
        );
    };

    return (
        <Modal open={open} onClose={() => !saving && onClose()}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 420, width: '92vw', p: { xs: 2, sm: 3 },
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                <ModalClose disabled={saving} />

                <Typography level="h4" sx={{ fontWeight: 800, mb: 0.3 }}>Админ чека</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }}>
                    Выбери, на кого записать чек — оклад и премии за день пересчитаются.
                    {data?.location_name ? ` ${data.location_name}` : ''}
                    {data?.date ? ` · ${fmtDate(data.date)}` : ''}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : (
                    <Stack spacing={0.7} sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        <Button variant="soft" color="neutral" disabled={saving}
                            onClick={() => onSelect(null)}
                            sx={{ justifyContent: 'flex-start', fontWeight: 700, fontSize: 13 }}>
                            Без привязки
                        </Button>

                        {onSchedule.length > 0 && (
                            <Typography sx={{
                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '0.06em', color: 'var(--joy-palette-text-tertiary)',
                                mt: 0.8, mb: 0.2,
                            }}>
                                По графику на эту дату
                            </Typography>
                        )}
                        {onSchedule.map(renderBtn)}

                        {offSchedule.length > 0 && (
                            <Typography sx={{
                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '0.06em', color: '#f59e0b',
                                mt: 1, mb: 0.2,
                            }}>
                                Вне графика на эту дату
                            </Typography>
                        )}
                        {offSchedule.map(renderBtn)}

                        {candidates.length === 0 && (
                            <Typography sx={{ fontSize: 12.5, color: 'var(--joy-palette-text-tertiary)', py: 2, textAlign: 'center' }}>
                                На эту точку и дату админы не назначены
                            </Typography>
                        )}
                    </Stack>
                )}

                <Button variant="plain" color="neutral" sx={{ mt: 1.5 }} disabled={saving}
                    onClick={onClose}>
                    Отмена
                </Button>
            </ModalDialog>
        </Modal>
    );
}